import {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Button, Flex, Tooltip, message as antdMessage } from "antd";
import clsx from "clsx";
import {
  SwapRightOutlined,
  ReloadOutlined,
  CopyOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import copy from "copy-to-clipboard";
import { useStyles } from "./styles";
import MarkdownRender from "./components/MarkdownRender";
import FunctionCallRender from "./components/FunctionCallRender";
import FileGallery from "../FileGallery";
import XAdkThoughtChain from "@/components/XAdkThoughtChain";
import { parseAgentMessage } from "@/utils";
import {
  mergeChatStrategies,
  resolvePresetMarkdownExtensions,
} from "@/presets/runtime";
import type {
  IMessage,
  XAdkChatbotProps,
  XAdkChatbotHandle,
  ChatGroup,
} from "@/types";
import type { ThoughtChainItemType } from "@/types/XAdkThoughtChain";
import { defaultToolKindResolver } from "@/types/FunctionCallRender";

const scrollThreshold = 10;

/**
 * XAdkChatbot - 增强版聊天组件
 *
 * 新增功能:
 * - ✅ 自动消息分组 (enableGrouping)
 * - ✅ 自动解析思维链 (enableProcessParsing)
 * - ✅ 文件展示 (FileGallery)
 * - ✅ 操作栏 (重试/复制/日志)
 * - ✅ 欢迎页面 (agentName/agentIcon/description)
 */
const XAdkChatbot = forwardRef<XAdkChatbotHandle, XAdkChatbotProps>(
  (
    {
      loading = false,
      prologue,
      suggestions,
      messages,
      showFnCallDetail,
      onConfirm,
      onSuggest,
      showRetry,
      showCopy,
      showLog,
      onRetry,
      onCopy,
      onShowLog,
      actions,
      actionsExtra,
      className,
      style,
      // welcome = null,
      enableGrouping = true,
      enableProcessParsing = true,
      parseOptions,
      initialized = true,
      sessionId,
      onFileClick,
      onResourceLinkClick,
      renderResourceLink,
      markdownExtensions,
      renderFunctionCall,
      toolKindResolver,
      strategies,
      preset,
      empty,
    },
    ref,
  ) => {
    const styles = useStyles();
    const listRef = useRef<HTMLDivElement>(null);
    const lastScrollTopRef = useRef(0);
    const userHasScrolledRef = useRef(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const prevInitializedRef = useRef(false);
    const prevSessionIdRef = useRef(sessionId);
    const previousMessageCountRef = useRef(messages.length);

    // 暴露命令式 API
    const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
      messagesEndRef.current?.scrollIntoView({ behavior });
    }, []);
    useImperativeHandle(ref, () => ({ scrollToBottom }), [scrollToBottom]);
    const mergedStrategies = useMemo(
      () => mergeChatStrategies(preset, strategies),
      [preset, strategies],
    );
    const mergedMarkdownExtensions = useMemo(
      () => resolvePresetMarkdownExtensions(preset, markdownExtensions),
      [markdownExtensions, preset],
    );
    const resolveToolKind = useCallback(
      (name?: string, msg?: IMessage) =>
        toolKindResolver?.(name) ??
        mergedStrategies.resolveToolKind?.({ name, msg }) ??
        defaultToolKindResolver(name),
      [toolKindResolver, mergedStrategies],
    );
    const parseProcessMessage =
      mergedStrategies.parseProcessMessage ?? parseAgentMessage;
    const resourceLinkSchemes = mergedStrategies.markdownResourceLinkSchemes;

    // 初始化完成时滚动到底部
    useEffect(() => {
      if (!initialized) return;
      if (!messages.length) return;
      if (prevInitializedRef.current) return;
      prevInitializedRef.current = true;

      const el = listRef.current;
      if (!el) return;

      // 用 MutationObserver 监听 DOM 子树变化，等内容渲染稳定后置底
      // 每次 DOM 变化都重新计时，确保所有异步渲染（Markdown、代码高亮、思维链等）完成后再滚动
      let stableTimer: ReturnType<typeof setTimeout>;
      const doScroll = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      };

      const observer = new MutationObserver(() => {
        clearTimeout(stableTimer);
        stableTimer = setTimeout(doScroll, 80);
      });

      observer.observe(el, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      // 立即执行一次，处理内容已经就绪的情况
      doScroll();
      // 兜底：最多等 500ms 后强制滚动并断开观察
      const maxTimer = setTimeout(() => {
        observer.disconnect();
        doScroll();
      }, 500);

      return () => {
        clearTimeout(stableTimer);
        clearTimeout(maxTimer);
        observer.disconnect();
      };
    }, [initialized, messages]);

    // 会话切换时重置滚动状态，允许重新触发初始化置底
    useEffect(() => {
      if (sessionId === undefined) return; // 未传 sessionId 不处理
      if (prevSessionIdRef.current === sessionId) return; // 同一会话不处理
      prevSessionIdRef.current = sessionId;
      prevInitializedRef.current = false;
      userHasScrolledRef.current = false;
    }, [sessionId]);

    // 新增用户消息代表一次主动发送，应无条件恢复跟随并置底。
    // 之后的流式回复继续沿用“用户未主动上滚时跟随”的规则。
    useEffect(() => {
      const previousCount = previousMessageCountRef.current;
      previousMessageCountRef.current = messages.length;
      const hasNewUserMessage = messages
        .slice(previousCount)
        .some((message) => message.role === "user");
      if (hasNewUserMessage) {
        userHasScrolledRef.current = false;
      }
      if (userHasScrolledRef.current) return;
      if (!messages.length) return;
      const el = listRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, [loading, messages]);

    // loading 结束时（流式输出完成），操作栏（metaFooter）会渲染出来增加高度，
    // 需要延迟滚动到底部，确保操作栏可见
    const prevLoadingRef = useRef(loading);
    useEffect(() => {
      const wasLoading = prevLoadingRef.current;
      prevLoadingRef.current = loading;

      if (!wasLoading || loading) return; // 只在 loading true → false 时触发
      if (userHasScrolledRef.current) return;
      if (!messages.length) return;

      // 延迟等待操作栏 DOM 渲染完成后再滚动
      const timer = setTimeout(() => {
        const el = listRef.current;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      }, 50);
      return () => clearTimeout(timer);
    }, [loading, messages]);

    // 处理滚动事件
    const handleScroll = useCallback(() => {
      const el = listRef.current;
      if (!el) return;
      const currentTop = el.scrollTop;

      if (currentTop < lastScrollTopRef.current) {
        userHasScrolledRef.current = true;
      }

      const isAtBottom =
        Math.abs(el.scrollHeight - currentTop - el.clientHeight) <=
        scrollThreshold;
      if (isAtBottom) {
        userHasScrolledRef.current = false;
      }

      lastScrollTopRef.current = currentTop;
    }, []);

    useEffect(() => {
      const listElement = listRef.current;
      if (!listElement) return;

      listElement.addEventListener("scroll", handleScroll);
      return () => {
        listElement.removeEventListener("scroll", handleScroll);
      };
    }, [handleScroll]);

    // ========== 消息分组逻辑 ==========
    const chatGroups = useMemo(() => {
      if (!enableGrouping) {
        // 不分组,每条消息独立
        return messages.map((msg) => ({
          id: msg.id,
          role: msg.role,
          msgs: [msg],
          invocationId: msg.invocationId,
          allFiles: msg.fileData || [],
          isLike: msg.isLike ?? 0,
        }));
      }

      const groups: ChatGroup[] = [];
      messages.forEach((msg) => {
        // 过滤 followup 消息
        if ((msg as any).role === "followup") return;

        const isRealUserQuery =
          msg.role === "user" && !msg.functionResponse && !msg.functionCall;
        const lastGroup = groups[groups.length - 1];
        const isLastGroupAgent = lastGroup?.role === "bot";

        if (isLastGroupAgent && !isRealUserQuery) {
          // 合并到上一个 bot 分组
          lastGroup.msgs.push(msg);
          if (msg.invocationId) lastGroup.invocationId = msg.invocationId;
          // 更新 isLike: 取最新的非0值,或保持当前值
          if (msg.isLike && msg.isLike !== 0) {
            lastGroup.isLike = msg.isLike;
          }
        } else {
          // 创建新分组
          groups.push({
            id: msg.id || `group-${groups.length}`,
            role: isRealUserQuery ? "user" : "bot",
            msgs: [msg],
            invocationId: msg.invocationId,
            allFiles: [],
            isLike: msg.isLike ?? 0,
          });
        }
      });

      // 合并文件
      groups.forEach((g) => {
        g.allFiles = g.msgs.reduce(
          (acc, m) => [...acc, ...(m.fileData || [])],
          [] as any[],
        );
      });

      return groups;
    }, [messages, enableGrouping]);

    // ========== 渲染 Bot 消息组 ==========
    const renderBotGroup = useCallback(
      (
        group: ChatGroup,
        isLastGroup: boolean,
        renderFunctionCall?: XAdkChatbotProps["renderFunctionCall"],
      ) => {
        const { msgs, allFiles } = group;

        if (!enableProcessParsing) {
          // 不解析 process,简单渲染
          return (
            <div key={group.id} className={styles.botMsg}>
              {msgs.map((msg, i) => {
                if (msg.text) {
                  return (
                    <MarkdownRender
                      key={`${msg.id}-${i}`}
                      text={msg.text}
                      resourceLinkSchemes={resourceLinkSchemes}
                      onFileClick={onFileClick}
                      onResourceLinkClick={onResourceLinkClick}
                      renderResourceLink={renderResourceLink}
                      markdownExtensions={mergedMarkdownExtensions}
                    />
                  );
                }
                if (msg.functionCall) {
                  return (
                    renderFunctionCall?.(msg) ?? (
                      <FunctionCallRender
                        key={`${msg.id}-${i}`}
                        msg={msg}
                        showDetail={showFnCallDetail}
                        onConfirm={onConfirm}
                        kind={resolveToolKind(msg.functionCall?.name, msg)}
                        renderApproval={mergedStrategies.renderApproval}
                        renderHandoff={mergedStrategies.renderHandoff}
                      />
                    )
                  );
                }
                return null;
              })}
            </div>
          );
        }

        // ========== 解析 Process 内容 ==========

        // 1. 合并工具调用
        const mergedToolMap = new Map<string, IMessage>();
        msgs.forEach((msg) => {
          if (msg.functionCall) {
            const callId = msg.functionCall.id || "";
            if (!mergedToolMap.has(callId)) {
              mergedToolMap.set(callId, { ...msg });
            } else {
              mergedToolMap.set(callId, {
                ...(mergedToolMap.get(callId) ?? {}),
                ...msg,
              });
            }
          } else if (msg.functionResponse) {
            const callId = msg.functionResponse.id || "";
            if (mergedToolMap.has(callId)) {
              const tool = mergedToolMap.get(callId);
              if (tool) tool.functionResponse = msg.functionResponse;
            } else {
              mergedToolMap.set(callId, {
                ...msg,
                functionCall: {
                  id: callId,
                  name: msg.functionResponse.name || "Unknown",
                  args: {},
                },
              });
            }
          }
        });

        // 2. 解析文本消息中的 process 内容
        type RenderNode =
          | { type: "text"; content: string; key: string }
          | { type: "process"; items: ThoughtChainItemType[]; key: string };

        const nodes: RenderNode[] = [];
        let currentProcessItems: ThoughtChainItemType[] = [];
        const processedToolIds = new Set<string>();

        const flushProcessItems = () => {
          if (currentProcessItems.length > 0) {
            nodes.push({
              type: "process",
              items: [...currentProcessItems],
              key: `process-${nodes.length}`,
            });
            currentProcessItems = [];
          }
        };

        msgs.forEach((msg) => {
          // 处理工具调用
          if (msg.functionCall) {
            const callId = msg.functionCall.id || "";
            if (!processedToolIds.has(callId)) {
              const mergedMsg = mergedToolMap.get(callId);
              if (mergedMsg) {
                currentProcessItems.push({
                  type: "tool",
                  key: `tool-${callId}`,
                  content: "",
                  msg: mergedMsg,
                });
                processedToolIds.add(callId);
              }
            }
          }
          // 处理文本消息
          else if (msg.text && !msg.functionResponse) {
            const parts = parseProcessMessage(msg.text, parseOptions as any);

            parts.forEach((part, partIdx) => {
              // process 内容放入 currentProcessItems
              if (
                [
                  "planning",
                  "replanning",
                  "reasoning",
                  "action_log",
                  "process_text",
                ].includes(part.type)
              ) {
                const titleMap: Record<string, string> = {
                  planning: "任务规划",
                  replanning: "重新规划",
                  reasoning: "推理分析",
                  action_log: "行动记录",
                  process_text: "过程分析",
                };

                currentProcessItems.push({
                  type: "text",
                  key: `${msg.id}-${partIdx}`,
                  content: part.content,
                  title: titleMap[part.type] || "分析",
                });
              }
              // 普通文本内容
              else {
                flushProcessItems();
                if (part.content.trim()) {
                  const lastNode = nodes[nodes.length - 1];
                  if (lastNode?.type === "text") {
                    // 合并到上一个文本节点
                    lastNode.content += "\n\n" + part.content;
                  } else {
                    nodes.push({
                      type: "text",
                      content: part.content,
                      key: `text-${msg.id}-${partIdx}`,
                    });
                  }
                }
              }
            });
          }
        });

        // 最后flush一次
        flushProcessItems();

        // 3. 准备操作栏数据
        const fullTextToCopy = msgs
          .filter((m) => !m.functionCall && !m.functionResponse)
          .map((m) => m.text || "")
          .join("");

        const lastBotMsg = msgs[msgs.length - 1];
        const hasProcess = nodes.some((n) => n.type === "process");
        const isGroupLoading = loading && isLastGroup;
        const actionProps = { message: group, isLastBotMsg: isLastGroup };
        const actionExtraNode = actionsExtra?.(actionProps);

        // 4. 渲染
        const lastProcessIdx = nodes.findLastIndex((n) => n.type === "process");
        return (
          <div key={group.id} className={styles.botMsg}>
            {nodes.map((node, idx) => {
              if (node.type === "process") {
                // 只有最后一个 process node 跟随 loading 状态，已完成的思维链不应显示 loading
                const isLastProcess = idx === lastProcessIdx;
                return (
                  <XAdkThoughtChain
                    key={node.key}
                    loading={isLastProcess && isGroupLoading}
                    title="思维链已完成"
                    items={node.items}
                    showFnCallDetail={showFnCallDetail}
                    onConfirm={onConfirm}
                    defaultOpen={isLastProcess && isGroupLoading}
                    renderFunctionCall={renderFunctionCall}
                    toolKindResolver={(name) => resolveToolKind(name)}
                    strategies={mergedStrategies}
                  />
                );
              }

              // 文本节点
              // const showBadge =
              //   hasProcess && nodes.findIndex((n) => n.type === "text") === idx;
              return (
                <div key={node.key}>
                  {/* {showBadge && (
                  <div className={styles.successBadge}>
                    <CheckCircleFilled /> 已完成所有规划任务
                  </div>
                )} */}
                  <MarkdownRender
                    text={node.content}
                    resourceLinkSchemes={resourceLinkSchemes}
                    onFileClick={onFileClick}
                    onResourceLinkClick={onResourceLinkClick}
                    renderResourceLink={renderResourceLink}
                    markdownExtensions={mergedMarkdownExtensions}
                  />
                </div>
              );
            })}

            {/* 文件展示 */}
            {allFiles.length > 0 && (
              <div className={styles.fileSection}>
                <div className={styles.fileHeader}>
                  <span>生成文件 ({allFiles.length})</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {allFiles.map((file, index) => (
                    <FileGallery
                      key={index}
                      file={file}
                      onClick={onFileClick}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* 操作栏 */}
            {!isGroupLoading &&
              (actions ||
                showRetry ||
                showCopy ||
                showLog ||
                actionExtraNode) && (
                <div className={styles.metaFooter}>
                  {actions ? (
                    actions(actionProps)
                  ) : (
                    <Flex gap={16} className={styles.actionIcons}>
                      {showRetry && isLastGroup && (
                        <Tooltip title="重新生成">
                          <ReloadOutlined onClick={onRetry} />
                        </Tooltip>
                      )}
                      {showCopy && (
                        <Tooltip title="复制内容">
                          <CopyOutlined
                            onClick={() => {
                              copy(fullTextToCopy);
                              antdMessage.success("复制成功");
                              onCopy?.(fullTextToCopy);
                            }}
                          />
                        </Tooltip>
                      )}
                      {showLog && lastBotMsg?.invocationId && (
                        <Tooltip title="查看日志">
                          <InfoCircleOutlined
                            onClick={() =>
                              onShowLog?.(
                                lastBotMsg.invocationId!,
                                lastBotMsg.timestamp,
                              )
                            }
                          />
                        </Tooltip>
                      )}
                    </Flex>
                  )}
                  {actionExtraNode}
                </div>
              )}
          </div>
        );
      },
      [
        enableProcessParsing,
        parseOptions,
        parseProcessMessage,
        showFnCallDetail,
        onConfirm,
        loading,
        showRetry,
        showCopy,
        showLog,
        onRetry,
        onCopy,
        onShowLog,
        actions,
        actionsExtra,
        onFileClick,
        onResourceLinkClick,
        renderResourceLink,
        mergedMarkdownExtensions,
        resourceLinkSchemes,
        styles,
        resolveToolKind,
        mergedStrategies,
      ],
    );

    // ========== 渲染用户消息组 ==========
    const renderUserGroup = useCallback(
      (group: ChatGroup) => {
        return (
          <div key={group.id} className={styles.userMsg}>
            {group.msgs.map((m, i) => (
              <div key={m.id || i} className={styles.userContainer}>
                {/* 文件展示 */}
                {m.fileData && m.fileData.length > 0 && (
                  <>
                    {m.fileData.map((file, index) => (
                      <FileGallery
                        key={index}
                        file={file}
                        align="left"
                        style={{ marginBottom: "16px" }}
                        onClick={onFileClick}
                      />
                    ))}
                  </>
                )}
                {/* 文本展示 */}
                {m.text && (
                  <div
                    className={styles.card}
                    onCopy={(event) => {
                      // 富文本标签的可见文案不含协议；复制用户消息时保留原始 Markdown，
                      // 使内容粘贴回 XAdkSender 后能重新还原成 Mention。
                      event.preventDefault();
                      event.clipboardData.setData("text/plain", m.text || "");
                    }}
                  >
                    <MarkdownRender
                      text={m.text}
                      compact
                      bareUrlAsText
                      highlightPlainMentions
                      resourceLinkSchemes={resourceLinkSchemes}
                      onFileClick={onFileClick}
                      onResourceLinkClick={onResourceLinkClick}
                      renderResourceLink={renderResourceLink}
                      markdownExtensions={mergedMarkdownExtensions}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      },
      [
        styles,
        onFileClick,
        onResourceLinkClick,
        renderResourceLink,
        mergedMarkdownExtensions,
        resourceLinkSchemes,
      ],
    );

    // ========== 空状态 ==========

    // ========== 渲染建议问题 ==========
    const renderSuggestions = () => {
      if (!suggestions || suggestions.length === 0) return null;

      return (
        <div className={styles.suggestionWrapper}>
          {suggestions.map((item) => (
            <div key={item} className={styles.suggestion}>
              <div className={styles.suggestContent}>
                <Button
                  type="text"
                  icon={<SwapRightOutlined />}
                  iconPlacement="end"
                  onClick={() => {
                    if (!item) return;
                    onSuggest?.(item);
                  }}
                  style={{
                    whiteSpace: "normal",
                    height: "auto",
                    wordWrap: "break-word",
                    textAlign: "left",
                    padding: "4px 15px",
                    lineHeight: "1.5",
                  }}
                >
                  {item}
                </Button>
              </div>
            </div>
          ))}
        </div>
      );
    };

    // 空状态判断
    const isEmpty =
      messages.length === 0 &&
      !prologue &&
      (!suggestions || suggestions.length === 0);

    return (
      <div className={clsx(styles.wrapper, className)} style={style}>
        <div className={styles.list} ref={listRef}>
          {/* 空状态 */}
          {isEmpty && empty && (
            <div className={styles.emptyWrapper}>
              {typeof empty === "function" ? empty() : empty}
            </div>
          )}

          {/* 开场白 */}
          {prologue && (
            <div className={styles.prologue}>
              <MarkdownRender
                text={prologue}
                resourceLinkSchemes={resourceLinkSchemes}
                onFileClick={onFileClick}
                onResourceLinkClick={onResourceLinkClick}
                renderResourceLink={renderResourceLink}
                markdownExtensions={mergedMarkdownExtensions}
              />
            </div>
          )}

          {/* 消息列表 */}
          {chatGroups.map((group, idx) =>
            group.role === "user"
              ? renderUserGroup(group)
              : renderBotGroup(
                  group,
                  idx === chatGroups.length - 1,
                  renderFunctionCall,
                ),
          )}

          {/* 建议问题 */}
          {renderSuggestions()}

          <div ref={messagesEndRef} />
        </div>
      </div>
    );
  },
);

XAdkChatbot.displayName = "XAdkChatbot";

export default XAdkChatbot;
